import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowRight,
    BadgeCheck,
    BookOpen,
    Building2,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    ClipboardCheck,
    FileText,
    Globe,
    GraduationCap,
    Layers,
    LineChart,
    MapPin,
    MessageCircle,
    Shield,
    ShieldCheck,
    Sparkles,
    Target,
    X,
} from "lucide-react";

/**
 * UniThink — Application & Admission Support Landing Page
 * React + Tailwind + (optional) Framer Motion animations
 * Drop-in component. No backend required.
 */

const cx = (...classes) => classes.filter(Boolean).join(" ");

const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.55, delay: 0.06 * i, ease: [0.22, 1, 0.36, 1] },
    }),
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.98 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, scale: 0.98, transition: { duration: 0.2 } },
};

function useScrollSpy(ids = []) {
    const [active, setActive] = useState(ids[0] || "");
    useEffect(() => {
        const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
        if (!els.length) return;

        const obs = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0));
                if (visible[0]?.target?.id) setActive(visible[0].target.id);
            },
            { root: null, rootMargin: "-35% 0px -55% 0px", threshold: [0.08, 0.12, 0.2, 0.35] }
        );

        els.forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, [ids.join("|")]);

    return active;
}

function Pill({ children, icon: Icon }) {
    return (
        <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-sm text-slate-700 shadow-sm backdrop-blur">
            {Icon ? <Icon className="h-4 w-4 text-slate-700" /> : null}
            {children}
        </span>
    );
}

function SectionTitle({ kicker, title, subtitle }) {
    return (
        <div className="mx-auto max-w-3xl text-center">
            {kicker ? (
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/60 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm">
                    <Sparkles className="h-3.5 w-3.5" />
                    {kicker}
                </div>
            ) : null}
            <h2 className="text-balance text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                {title}
            </h2>
            {subtitle ? (
                <p className="mt-3 text-pretty text-slate-600">{subtitle}</p>
            ) : null}
        </div>
    );
}

function Card({ className, children }) {
    return (
        <div
            className={cx(
                "rounded-2xl border border-slate-200 bg-white shadow-sm",
                "transition hover:shadow-md",
                className
            )}
        >
            {children}
        </div>
    );
}

function IconBadge({ icon: Icon, title, desc }) {
    return (
        <div className="flex gap-3">
            <div className="mt-0.5 flex h-10 w-10 flex-none items-center justify-center rounded-xl border border-slate-200 bg-slate-50">
                <Icon className="h-5 w-5 text-slate-900" />
            </div>
            <div>
                <div className="font-medium text-slate-900">{title}</div>
                <div className="text-sm text-slate-600">{desc}</div>
            </div>
        </div>
    );
}

function FAQItem({ q, a, open, onToggle }) {
    return (
        <Card className="overflow-hidden">
            <button
                onClick={onToggle}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
            >
                <span className="font-medium text-slate-900">{q}</span>
                {open ? (
                    <ChevronUp className="h-5 w-5 text-slate-600" />
                ) : (
                    <ChevronDown className="h-5 w-5 text-slate-600" />
                )}
            </button>
            <AnimatePresence initial={false}>
                {open ? (
                    <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="border-t border-slate-200"
                    >
                        <div className="px-5 py-4 text-sm leading-relaxed text-slate-600">{a}</div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </Card>
    );
}

function Modal({ open, onClose, title, children }) {
    return (
        <AnimatePresence>
            {open ? (
                <motion.div
                    className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <div
                        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    <motion.div
                        variants={scaleIn}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                        className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
                        role="dialog"
                        aria-modal="true"
                        aria-label={title}
                    >
                        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                            <div className="font-semibold text-slate-900">{title}</div>
                            <button
                                onClick={onClose}
                                className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700 transition hover:bg-slate-50"
                                aria-label="Close"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="px-5 py-5">{children}</div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

function Toast({ show, message, onClose }) {
    return (
        <AnimatePresence>
            {show ? (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.25 }}
                    className="fixed bottom-4 left-1/2 z-[60] w-[min(560px,calc(100%-2rem))] -translate-x-1/2"
                >
                    <div className="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 rounded-xl bg-slate-900 p-2 text-white">
                                <CheckCircle2 className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-medium text-slate-900">Submitted.</div>
                                <div className="text-sm text-slate-600">{message}</div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                        >
                            Close
                        </button>
                    </div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

export default function UniThinkApplicationAdmissionSupportLanding() {
    const sectionIds = useMemo(
        () => ["overview", "included", "how", "scope", "pricing", "faqs"],
        []
    );
    const active = useScrollSpy(sectionIds);

    const [openModal, setOpenModal] = useState(false);
    const [toast, setToast] = useState(false);
    const [faqOpen, setFaqOpen] = useState(0);

    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [intake, setIntake] = useState("Fall / Spring coming up");
    const [countries, setCountries] = useState("");
    const [scores, setScores] = useState("");

    const handlePayment = () => {
        if (!name || !phone || !email) {
            alert("Please fill all required details (Name, Phone, Email).");
            return;
        }
        const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY,
            amount: 5723000, // ₹48,500 in paise
            currency: "INR",
            name: "UniThink",
            description: "Application & Admission Support",
            handler: function (response) {
                // Send data to Google Sheets
                fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                    method: "POST",
                    mode: "no-cors",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        name: name,
                        phone: phone,
                        intake: intake,
                        countries: countries,
                        scores: scores,
                        payment_status: "Paid",
                        paymentId: response.razorpay_payment_id,
                        service: "Application & Admission Support",
                        amount: 57230
                    }),
                }).finally(() => {
                    alert("Payment Successful! We have received your request.");
                    window.location.href = "/";
                });
            },
            prefill: {
                name: name,
                contact: phone,
            },
            theme: { color: "#0f172a" },
            modal: {
                ondismiss: function () {
                    fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                        method: "POST",
                        mode: "no-cors",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            name: name,
                            phone: phone,
                            intake: intake,
                            countries: countries,
                            scores: scores,
                            payment_status: "Abandoned",
                            paymentId: "N/A",
                            service: "Application & Admission Support",
                            amount: 57230
                        }),
                    });
                }
            }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
            fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                method: "POST",
                mode: "no-cors",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: name,
                    phone: phone,
                    intake: intake,
                    countries: countries,
                    scores: scores,
                    payment_status: "Payment Failed",
                    paymentId: response.error.metadata.payment_id || "N/A",
                    service: "Application & Admission Support",
                    amount: 57230
                }),
            });
        });
        rzp.open();
    };

    const nav = [
        { id: "overview", label: "Overview" },
        { id: "included", label: "Included" },
        { id: "how", label: "How it Works" },
        { id: "scope", label: "Scope" },
        { id: "pricing", label: "Pricing" },
        { id: "faqs", label: "FAQs" },
    ];

    const faqs = [
        {
            q: "Does this include writing my SOP for me?",
            a: "This package provides SOP guidance and refinement support. Final ownership remains with the student.",
        },
        {
            q: "Can you help with LOR format and content direction?",
            a: "Yes, guidance is included so LORs match program expectations.",
        },
        {
            q: "Is scholarship guidance included?",
            a: "Basic guidance may be provided; dedicated scholarship work can be defined separately if needed.",
        },
        {
            q: "How many universities are covered?",
            a: "Up to 5 universities per intake, as stated in the scope.",
        },
        {
            q: "Can I spread universities across multiple countries?",
            a: "Yes, this is designed for multi-country applications.",
        },
        {
            q: "Will you guarantee admission?",
            a: "No. UniThink strengthens strategy and execution; universities decide outcomes.",
        },
        {
            q: "What if I want to apply to more than 5 universities?",
            a: "Additional universities can be priced separately or moved into a higher scope package.",
        },
        {
            q: "What happens after I receive admits?",
            a: "Support for decision-making can be continued via counselling sessions or a defined add-on.",
        },
    ];

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="min-h-screen bg-white text-slate-900">
            {/* Top glow */}
            <div className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-80 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(15,23,42,0.08)_0%,rgba(15,23,42,0)_70%)]" />

            {/* Header */}
            <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
                    <div className="flex items-center gap-3">
                        <div className="grid h-10 w-10 place-items-center rounded-2xl bg-slate-900 text-white shadow-sm">
                            <Globe className="h-5 w-5" />
                        </div>
                        <div className="leading-tight">
                            <div className="text-sm font-semibold">UniThink</div>
                            <div className="text-xs text-slate-500">Application & Admission Support</div>
                        </div>
                    </div>

                    <nav className="hidden items-center gap-1 lg:flex">
                        {nav.map((n) => (
                            <button
                                key={n.id}
                                onClick={() => scrollTo(n.id)}
                                className={cx(
                                    "rounded-xl px-3 py-2 text-sm transition",
                                    active === n.id
                                        ? "bg-slate-900 text-white"
                                        : "text-slate-700 hover:bg-slate-100"
                                )}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => scrollTo("pricing")}
                            className="hidden rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50 sm:inline-flex"
                        >
                            View Pricing
                        </button>
                        <button
                            onClick={() => setOpenModal(true)}
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                        >
                            Proceed with Applications <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <main>
                <section className="relative">
                    <div className="mx-auto max-w-6xl px-4 pb-10 pt-10 sm:px-6 sm:pb-14 sm:pt-14">
                        <div className="grid items-start gap-10 lg:grid-cols-[1.2fr_0.8fr]">
                            <div>
                                <motion.div
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(0)}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm"
                                >
                                    <BadgeCheck className="h-4 w-4" /> End-to-end execution support (multi-country)
                                </motion.div>

                                <motion.h1
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(1)}
                                    className="mt-4 text-balance text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl"
                                >
                                    Application & Admission Support
                                </motion.h1>

                                <motion.p
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(2)}
                                    className="mt-4 text-pretty text-slate-600"
                                >
                                    End-to-end support for students ready to apply—covering multi-country shortlisting,
                                    SOP/LOR guidance, application execution, tracking, and follow-ups.
                                </motion.p>

                                <motion.div
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(3)}
                                    className="mt-6 flex flex-wrap gap-2"
                                >
                                    <Pill icon={Globe}>Multi-country shortlisting</Pill>
                                    <Pill icon={FileText}>SOP/LOR guidance</Pill>
                                    <Pill icon={ClipboardCheck}>Execution support</Pill>
                                    <Pill icon={Layers}>Tracking + follow-ups</Pill>
                                    <Pill icon={MessageCircle}>Dedicated counsellor</Pill>
                                </motion.div>

                                <motion.div
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate={fadeUp.show(4)}
                                    className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center"
                                >
                                    <button
                                        onClick={() => setOpenModal(true)}
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                    >
                                        Proceed with Applications <ArrowRight className="h-5 w-5" />
                                    </button>
                                    <button
                                        onClick={() => scrollTo("included")}
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50"
                                    >
                                        What’s Included <ChevronDown className="h-5 w-5" />
                                    </button>
                                    <div className="text-sm text-slate-500 sm:ml-2">
                                        Covers up to <span className="font-semibold text-slate-700">5 universities</span> per intake.
                                    </div>
                                </motion.div>
                            </div>

                            {/* Credibility + scope panel */}
                            <motion.div
                                variants={fadeUp}
                                initial="hidden"
                                animate={fadeUp.show(2)}
                                className="lg:pt-3"
                            >
                                <Card className="overflow-hidden">
                                    <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
                                        <div className="flex items-center justify-between">
                                            <div className="font-semibold">Coverage</div>
                                            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200">
                                                <Shield className="h-3.5 w-3.5" /> Scope clarity
                                            </span>
                                        </div>
                                        <p className="mt-1 text-sm text-slate-600">Covers up to 5 universities per intake.</p>
                                    </div>
                                    <div className="space-y-4 px-5 py-5">
                                        <IconBadge
                                            icon={Globe}
                                            title="Multi-country applications"
                                            desc="Shortlisting inputs across multiple countries, aligned to goals and eligibility."
                                        />
                                        <IconBadge
                                            icon={FileText}
                                            title="SOP/LOR guidance"
                                            desc="Structure, story, alignment to target programs — refine, don’t random-write."
                                        />
                                        <IconBadge
                                            icon={Layers}
                                            title="Tracking + follow-ups"
                                            desc="Dashboard approach for deadlines, document status, and submission checkpoints."
                                        />
                                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                            <div className="text-sm font-semibold text-slate-900">Bottom line</div>
                                            <p className="mt-1 text-sm text-slate-600">
                                                Reduce avoidable errors, strengthen submissions, and manage the intake process like a pro.
                                            </p>
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Overview / Problem context */}
                <section id="overview" className="border-t border-slate-200 bg-slate-50/60">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="Why this exists"
                            title="When you’re ready to apply, execution is the difference"
                            subtitle="Global applications are getting more competitive. Once you decide to apply, missed deadlines, weak documentation, or inconsistent SOP narratives can reduce your chances."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-3">
                            <Card className="p-6">
                                <div className="flex items-start gap-3">
                                    <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                        <Globe className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">Competition is rising</div>
                                        <p className="mt-1 text-sm text-slate-600">
                                            UNESCO reports 6.9 million students study outside their home country — mobility has tripled since 2000.
                                        </p>
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-start gap-3">
                                    <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                        <Target className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">Mismatch risk is real</div>
                                        <p className="mt-1 text-sm text-slate-600">
                                            OECD analysis shows many students struggle to align career expectations with education plans — skills mismatches follow.
                                        </p>
                                    </div>
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-start gap-3">
                                    <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                        <ClipboardCheck className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold">Execution matters</div>
                                        <p className="mt-1 text-sm text-slate-600">
                                            Errors, missed deadlines, weak docs, or inconsistent narratives can lower admit probability — even with a strong profile.
                                        </p>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        <div className="mt-8 grid gap-6 lg:grid-cols-2">
                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <Sparkles className="h-4 w-4" /> What this service does
                                </div>
                                <p className="mt-2 text-sm text-slate-600">
                                    This service supports students who are ready to apply by providing structured execution support—shortlisting inputs,
                                    documentation guidance, end-to-end application assistance, and consistent tracking.
                                    The objective: reduce avoidable errors, strengthen submission quality, and manage the intake cycle professionally.
                                </p>
                            </Card>
                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <Shield className="h-4 w-4" /> What you’re really buying
                                </div>
                                <p className="mt-2 text-sm text-slate-600">
                                    A dedicated counsellor + a clean system for documents, deadlines, checks, and follow-ups. Not chaos. Not “last day panic”.
                                </p>
                            </Card>
                        </div>
                    </div>
                </section>

                {/* Included */}
                <section id="included" className="bg-white">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="What’s included"
                            title="Shortlist smart. Write strong. Submit clean. Track everything."
                            subtitle="Everything you need once you’ve decided to apply."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-3">
                            {[
                                {
                                    icon: Globe,
                                    title: "Multi-country shortlisting guidance",
                                    desc: "Aligned to goals + eligibility across multiple countries.",
                                },
                                {
                                    icon: ClipboardCheck,
                                    title: "Up to 5 universities per intake",
                                    desc: "End-to-end application assistance (execution support).",
                                },
                                {
                                    icon: FileText,
                                    title: "SOP/LOR guidance",
                                    desc: "Structure, story, and alignment to target programs.",
                                },
                                {
                                    icon: Layers,
                                    title: "Tracking dashboard approach",
                                    desc: "Deadlines, document status, submissions — clear visibility.",
                                },
                                {
                                    icon: MessageCircle,
                                    title: "Follow-ups + coordination",
                                    desc: "Guidance for status movement and next actions.",
                                },
                                {
                                    icon: GraduationCap,
                                    title: "Dedicated counsellor continuity",
                                    desc: "One point of contact through the entire intake cycle.",
                                },
                            ].map((it, idx) => (
                                <motion.div
                                    key={it.title}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView={fadeUp.show(idx)}
                                    viewport={{ once: true, amount: 0.25 }}
                                >
                                    <Card className="p-6">
                                        <div className="flex items-start gap-3">
                                            <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                                <it.icon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-slate-900">{it.title}</div>
                                                <p className="mt-1 text-sm text-slate-600">{it.desc}</p>
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                            <button
                                onClick={() => setOpenModal(true)}
                                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                            >
                                Proceed with Applications <ArrowRight className="h-5 w-5" />
                            </button>
                            <button
                                onClick={() => scrollTo("how")}
                                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-800 transition hover:bg-slate-50"
                            >
                                See How It Works <ChevronDown className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </section>

                {/* How it works */}
                <section id="how" className="border-t border-slate-200 bg-slate-50/60">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="How it works"
                            title="Four steps. No confusion."
                            subtitle="A clean execution pipeline from intake to follow-ups."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-4">
                            {[
                                {
                                    icon: ClipboardCheck,
                                    title: "Step 1: Intake + eligibility review",
                                    desc: "Define target programs and countries.",
                                },
                                {
                                    icon: Target,
                                    title: "Step 2: Shortlist + application plan",
                                    desc: "Deadlines, documents, sequencing.",
                                },
                                {
                                    icon: FileText,
                                    title: "Step 3: SOP/LOR guidance + build",
                                    desc: "Narrative consistency across universities.",
                                },
                                {
                                    icon: Layers,
                                    title: "Step 4: Submit + track + follow-up",
                                    desc: "Checks, tracking, and follow-ups through the intake cycle.",
                                },
                            ].map((it, idx) => (
                                <motion.div
                                    key={it.title}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView={fadeUp.show(idx)}
                                    viewport={{ once: true, amount: 0.25 }}
                                >
                                    <Card className="p-6">
                                        <div className="flex items-start gap-3">
                                            <div className="rounded-2xl bg-slate-900 p-3 text-white">
                                                <it.icon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-slate-900">{it.title}</div>
                                                <p className="mt-1 text-sm text-slate-600">{it.desc}</p>
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-10 grid gap-6 lg:grid-cols-1">
                            <Card className="flex flex-col items-center p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <CheckCircle2 className="h-4 w-4" /> Outcomes you can expect
                                </div>
                                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                                    {[
                                        "Reduced application errors and missed deadlines",
                                        "Stronger SOP narrative consistency across universities",
                                        "Better shortlist discipline (priority tiers by fit and feasibility)",
                                        "Less stress for students and parents during submissions",
                                        "Clear tracking and status visibility through the cycle",
                                    ].map((x) => (
                                        <li key={x} className="flex gap-2">
                                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
                                            <span>{x}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>


                        </div>

                        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
                            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                                <div>
                                    <div className="text-lg font-semibold text-slate-900">Ready to apply without last-day panic?</div>
                                    <div className="mt-1 text-sm text-slate-600">
                                        Submit clean applications with professional tracking.
                                    </div>
                                </div>
                                <button
                                    onClick={() => setOpenModal(true)}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                >
                                    Proceed with Applications <ArrowRight className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Scope + Notes */}
                <section id="scope" className="border-t border-slate-200 bg-white">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="Scope & notes"
                            title="Scope clarity (so expectations stay clean)"
                            subtitle="This keeps the process smooth for both families and counsellors."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-2">
                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <Shield className="h-4 w-4" /> Coverage statement
                                </div>
                                <p className="mt-2 text-sm text-slate-600">
                                    Covers up to <span className="font-semibold text-slate-700">5 universities per intake</span>.
                                    Additional universities can be scoped separately.
                                </p>

                                <div className="mt-5 grid gap-3">
                                    {["Multi-country shortlisting support", "SOP/LOR guidance", "Execution + tracking", "Follow-ups through intake"].map(
                                        (x) => (
                                            <div key={x} className="flex gap-2 text-sm text-slate-600">
                                                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
                                                <span>{x}</span>
                                            </div>
                                        )
                                    )}
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <ClipboardCheck className="h-4 w-4" /> Important notes
                                </div>
                                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                                    {[
                                        "University application fees, courier charges, visa fees, embassy fees, and third-party service charges are not included unless explicitly stated.",
                                        "Admission decisions remain the institution’s discretion.",
                                    ].map((x) => (
                                        <li key={x} className="flex gap-2">
                                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
                                            <span>{x}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        </div>
                    </div>
                </section>

                {/* Pricing */}
                <section id="pricing" className="border-t border-slate-200 bg-slate-50/60">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="Price & positioning"
                            title="₹48,500 + 18% GST per intake (up to 5 universities)"
                            subtitle="Best for students ready to apply who want structured execution, strong documentation, and clean tracking."
                        />

                        <div className="mt-10 grid gap-6 lg:grid-cols-[1fr_1fr]">
                            <Card className="p-6">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                                            <ClipboardCheck className="h-4 w-4" /> Intake package
                                        </div>
                                        <div className="mt-4 text-3xl font-semibold tracking-tight text-slate-900">₹48,500 + 18% GST</div>
                                        <div className="mt-1 text-sm text-slate-600">per intake (up to 5 universities)</div>
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-white p-3">
                                        <div className="text-xs font-semibold text-slate-600">Coverage</div>
                                        <div className="mt-1 text-sm font-semibold text-slate-900">Up to 5 universities</div>
                                    </div>
                                </div>

                                <div className="mt-6 space-y-2 text-sm text-slate-600">
                                    {[
                                        "Multi-country shortlisting inputs",
                                        "SOP/LOR guidance + refinement",
                                        "End-to-end application execution support",
                                        "Tracking + follow-ups",
                                        "Dedicated counsellor continuity",
                                    ].map((x) => (
                                        <div key={x} className="flex gap-2">
                                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" />
                                            <span>{x}</span>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={() => setOpenModal(true)}
                                    className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                >
                                    Proceed with Applications <ArrowRight className="h-5 w-5" />
                                </button>

                                <div className="mt-3 text-center text-xs text-slate-500">
                                    Add your policy: timelines, rescheduling, validity, and add-on pricing.
                                </div>
                            </Card>

                            <Card className="p-6">
                                <div className="text-sm font-semibold text-slate-900">Recommended reading</div>
                                <div className="mt-4 space-y-3">
                                    {[
                                        "Career uncertainty: why it happens and how to reduce it",
                                        "Shortlisting strategy: fit vs ranking vs ROI",
                                        "Common admissions timeline mistakes (India and abroad)",
                                        "Smart study abroad decision- Episode 1",
                                    ].map((x) => (
                                        <div
                                            key={x}
                                            className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700 transition hover:bg-slate-50"
                                        >
                                            <div className="font-semibold text-slate-900">{x}</div>
                                            <div className="mt-1 text-xs text-slate-500">Comming soon</div>
                                        </div>
                                    ))}
                                </div>


                            </Card>
                        </div>
                    </div>
                </section>

                {/* FAQs */}
                <section id="faqs" className="border-t border-slate-200 bg-white">
                    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                        <SectionTitle
                            kicker="FAQs"
                            title="Clear answers (so your SOP doesn’t become a panic attack)"
                            subtitle="We keep it practical, not fluffy."
                        />

                        <div className="mt-10 grid gap-4 lg:grid-cols-2">
                            {faqs.map((f, i) => (
                                <FAQItem
                                    key={f.q}
                                    q={f.q}
                                    a={f.a}
                                    open={faqOpen === i}
                                    onToggle={() => setFaqOpen((p) => (p === i ? -1 : i))}
                                />
                            ))}
                        </div>

                        <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                            <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                                <div>
                                    <div className="text-lg font-semibold text-slate-900">
                                        Apply with a structured process and professional tracking.
                                    </div>
                                    <div className="mt-1 text-sm text-slate-600">Start your Application & Admission Support today.</div>
                                </div>
                                <button
                                    onClick={() => setOpenModal(true)}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                                >
                                    Proceed with Applications <ArrowRight className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

            </main>

            {/* Modal */}
            <Modal
                open={openModal}
                onClose={() => setOpenModal(false)}
                title="Proceed with Applications — Get Started"
            >
                <div className="grid gap-4 sm:grid-cols-2">
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Student Name</span>
                        <input
                            value={name} onChange={(e) => setName(e.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., Arjun"
                        />
                    </label>
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Phone Number</span>
                        <input
                            value={phone} onChange={(e) => setPhone(e.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., +91 XXXXX XXXXX"
                        />
                    </label>
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Email Address</span>
                        <input
                            type="email"
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., student@gmail.com"
                        />
                    </label>
                    <label className="grid gap-1 sm:col-span-2">
                        <span className="text-xs font-medium text-slate-700">Target intake</span>
                        <select value={intake} onChange={(e) => setIntake(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400">
                            <option>Fall / Spring coming up</option>
                            <option>Next year</option>
                            <option>Applying currently (need immediate help)</option>
                            <option>Not sure</option>
                        </select>
                    </label>
                    <label className="grid gap-1 sm:col-span-2">
                        <span className="text-xs font-medium text-slate-700">Countries / Programs in mind</span>
                        <input
                            value={countries} onChange={(e) => setCountries(e.target.value)}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., US, UK, Germany (MS in CS)"
                        />
                    </label>
                    <label className="grid gap-1 sm:col-span-2">
                        <span className="text-xs font-medium text-slate-700">Any test scores?</span>
                        <textarea
                            value={scores} onChange={(e) => setScores(e.target.value)}
                            rows={2}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., IELTS 7.5, GRE 315, or 'planning to take'"
                        />
                    </label>
                </div>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-slate-500">
                        Next step: connect this form to WhatsApp / Google Sheets.
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setOpenModal(false)}
                            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                        >
                            Not now
                        </button>
                        <button
                            onClick={handlePayment}
                            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:translate-y-[-1px]"
                        >
                            Submit <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </Modal>

            <Toast
                show={toast}
                message="Request captured. (This demo is ready to connect to your backend)."
                onClose={() => setToast(false)}
            />
        </div>
    );
}
